// /components/VideoSection.jsx

import React from "react";
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

import VideoJS from "./VideoJS"; // Video.js 컴포넌트 (same folder)
import IconButton from "@mui/material/IconButton";

const placeholderStyle = {
  width: "100%",
  aspectRatio: "4 / 3",
  backgroundColor: "#e0e0e0",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

/**
 * 영상 재생 영역 + 영상 리스트 + 페이지네이션
 *
 * @param {Object} props
 *  - selectedVideo: {id, title, description, videoSrc} | null
 *  - videoItems: 현재 페이지에 보여줄 비디오 배열
 *  - totalPages: 총 페이지 수
 *  - currentPage: 현재 페이지 번호
 *  - onSelectVideo: (video) => void
 *  - onPrevPage: () => void
 *  - onNextPage: () => void
 *  - videoJsOptions: Video.js 재생 옵션
 *  - onPlayerReady: (player) => void
 */
const VideoSection = ({
  selectedVideo,
  videoItems,
  totalPages,
  currentPage,
  onSelectVideo,
  onPrevPage,
  onNextPage,
  videoJsOptions,
  onPlayerReady,
}) => {
  return (
    <Box
      sx={{ width: "100%", maxWidth: "1200px", mt: 4, px: { xs: 2, md: 0 } }}
    >
      <Grid container spacing={4}>
        {/* 왼쪽: 플레이어 or iframe */}
        <Grid item xs={12} md={6}>
          {selectedVideo ? (
            <VideoJS options={videoJsOptions} onReady={onPlayerReady} />
          ) : (
            <Box sx={placeholderStyle}>
              <Typography>영상이 없습니다.</Typography>
            </Box>
          )}
        </Grid>

        {/* 오른쪽: 영상 리스트 + 페이지 버튼 */}
        <Grid item xs={12} md={6}>
          <Box sx={{ width: "100%" }}>
            <List sx={{ width: "100%", padding: 0 }}>
              {videoItems.map((video) => {
                const isActive = selectedVideo?.id === video.id;
                return (
                  <ListItem key={video.id} sx={{ padding: 0 }}>
                    <ListItemButton
                      onClick={() => onSelectVideo(video)}
                      sx={{
                        borderRadius: "9999px",
                        textAlign: "left",
                        fontSize: { xs: "0.75rem", md: "1rem" },
                        py: { xs: "7px", md: "8px" },
                        px: { xs: "12px", md: "24px" },
                        mb: "8px",
                        cursor: "pointer",
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                        backgroundColor: isActive ? "#8B0000" : "#fff",
                        color: isActive ? "#fff" : "#000",
                        "&:hover": {
                          backgroundColor: isActive ? "#8B0000" : "#fff",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: { xs: "4px", md: "16px" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                            flexGrow: 1,
                            gap: { xs: "4px", md: "16px" },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: { xs: "0.875rem", md: "1rem" },
                            }}
                            noWrap
                          >
                            {video.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: { xs: "0.75rem", md: "1rem" },
                            }}
                            noWrap
                          >
                            {video.description}
                          </Typography>
                        </Box>
                        <PlayCircleIcon
                          sx={{
                            fontSize: { xs: "24px", md: "32px" },
                            color: "#fff",
                          }}
                        />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          {/* 위/아래 페이지 버튼 */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                mt: 2,
                paddingBottom: "32px",
              }}
            >
              <IconButton
                onClick={onPrevPage}
                disabled={currentPage === 1}
                sx={{
                  backgroundColor: "white",
                  color: `${currentPage === 1 ? "gray" : "red"} !important`,
                  border: `2px solid ${
                    currentPage === 1 ? "gray" : "red"
                  } !important`,
                  borderRadius: "50%",
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                  width: { xs: "32px", md: "36px" },
                  height: { xs: "32px", md: "36px" },
                }}
              >
                <KeyboardArrowUpIcon
                  sx={{
                    fontSize: { xs: "32px", md: "36px" },
                    color: "inherit",
                  }}
                />
              </IconButton>

              <IconButton
                onClick={onNextPage}
                disabled={currentPage === totalPages}
                sx={{
                  backgroundColor: "white",
                  color: `${
                    currentPage === totalPages ? "gray" : "red"
                  } !important`,
                  border: `2px solid ${
                    currentPage === totalPages ? "gray" : "red"
                  } !important`,
                  borderRadius: "50%",
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                  width: { xs: "32px", md: "36px" },
                  height: { xs: "32px", md: "36px" },
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: { xs: "32px", md: "36px" },
                    color: "inherit",
                  }}
                />
              </IconButton>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default VideoSection;
